import { useMemo, useContext } from "react"
import { useLocation } from "react-router"
import { WebBootContext } from "source/publishing/WebBoot"
import { useSession } from "source/shared/hooks/useSession"
import {
  useNotificationsChannel,
  useChatChannel,
} from "source/shared/contexts/JoltContext"
import { useLayoutTheme } from "source/shared/contexts/ThemeContext"
import { extractMenuItems } from "source/shared/extractMenuItems"
import { useAvatarColors } from "source/shared/components/Avatar"
import {
  Header as NavHeader,
  useNotificationCount,
  useChatHeaderProps,
} from "source/header"
import { mapReachMenuLinkProps } from "./mapNavLinkProps"
import { BREAKPOINTS } from "source/shared/hooks/useWindowDimensions"
import { useHomePageForSession } from "source/shared/hooks/useHomePageForSession"

export function HydratedHeader() {
  const visible = !useIsFallbackHome()
  const { currentOrganization } = useContext(WebBootContext)
  const hasDarkModeAvatar =
    !!currentOrganization.attributes.dark_mode_avatar_url
  const location = useLocation()
  const isLoginPage = location.pathname === "/login"

  const opacity = visible === true ? "1.0" : "0.0"
  const headerCss = useMemo(
    () => ({
      opacity,
      transition: "opacity 0.4s ease",
      [`@media screen and (max-width: ${BREAKPOINTS.LG - 1}px)`]: {
        ".sermon-episode-layout &": {
          display: "none",
        },
      },
      ...(hasDarkModeAvatar && {
        '[data-color-scheme="dark"][data-contrast="low"] & .Header__org-avatar':
          {
            backgroundColor: "transparent",
          },
      }),
      ...(isLoginPage && {
        ".Header__org-wrapper": {
          display: "none",
        },
        ".Header__layout": {
          justifyContent: "center",
        },
      }),
    }),
    [opacity, hasDarkModeAvatar, isLoginPage],
  )
  return (
    <div css={headerCss}>
      <NavHeader {...useHeaderPropsBuiltFromPublishingWebBoot()} />
    </div>
  )
}

function useIsFallbackHome() {
  const location = useLocation()
  const page = useHomePageForSession()
  return location.pathname === "/home" && !page
}

function useHeaderPropsBuiltFromPublishingWebBoot() {
  const { search, pathname } = useLocation()
  const { currentOrganization, draftMenu, draftMode, publishedMenu } =
    useContext(WebBootContext)
  const {
    data: currentPerson,
    meta: { authenticated: personIdentified },
  } = useSession(false)
  const notificationsChannel = useNotificationsChannel()
  const chatChannel = useChatChannel()
  const theme = useLayoutTheme()

  const menu = draftMode
    ? extractMenuItems(draftMenu)
    : extractMenuItems(publishedMenu)
  const { foregroundColor, backgroundColor } = useAvatarColors({
    isHeader: true,
  })
  const organizationDisplayName = currentOrganization.attributes.name
  const lightModeAvatarUrl = currentOrganization.attributes.avatar_url
  const darkModeAvatarUrl = currentOrganization.attributes.dark_mode_avatar_url
  const organizationImageUrl =
    theme === "dark" && darkModeAvatarUrl
      ? darkModeAvatarUrl
      : lightModeAvatarUrl
  const personDisplayName = currentPerson.attributes.first_name
  const personImageUrl =
    currentPerson.attributes.avatar_url +
    `?g=120x120%23&background_color=${backgroundColor}&color=${foregroundColor}&theme=${theme}`

  // NOTE: This isn't truly reactive... but it seems to work 'good enough' for today.
  const notificationDelay = pathname.startsWith("/notifications") ? 0 : 1000

  const notificationCount = useNotificationCount({
    personIdentified,
    notificationsChannel,
    notificationDelay,
  })

  const { chatUnread, pcoChatEnabled } = useChatHeaderProps({
    personIdentified,
    personId: currentPerson.id,
    channel: chatChannel,
    delay: 1000,
  })

  const automaticallyAppendedLinks = ["/profile", "/logout", "/login"]
  const links = menu
    .filter(
      ({ href }: { href: string }) =>
        !automaticallyAppendedLinks.includes(href),
    )
    .map(mapReachMenuLinkProps)

  const returnUrl = new URLSearchParams(search).get("return")
  const loginLink = mapReachMenuLinkProps({
    href:
      pathname === "/login" && !returnUrl
        ? pathname + search
        : `/login?return=${returnUrl || pathname + search}`,
    children: "Log in",
  })

  const organizationLink = mapReachMenuLinkProps({
    href: "/",
  })

  const notificationLink = mapReachMenuLinkProps({
    href: "/notifications",
  })

  const myChurchCenterLink = mapReachMenuLinkProps({ href: "/me" })

  return {
    links,
    loginLink,
    myChurchCenterLink,
    notificationCount,
    notificationLink,
    organizationDisplayName,
    organizationImageUrl,
    organizationLink,
    pcoChatEnabled,
    personDisplayName,
    personIdentified,
    personImageUrl,
    chatUnread,
  }
}
